import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Pencil, Trash2, ImageIcon, Share2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhotoModal } from '@/components/photos/PhotoModal';
import {
  EditAlbumDialog,
  DeleteAlbumDialog,
  AddPhotosToAlbumDialog,
  SortableAlbumPhotos,
  AlbumShareDialog,
  CollaboratorsPanel,
  InvitationDialog,
} from '@/components/albums';
import { useAlbum, useAlbumPhotos } from '@/hooks/useAlbums';
import { useCollaborators, isAlbumOwner } from '@/hooks/useCollaborators';
import { useUserPlan, isFreePlan } from '@/hooks/usePlan';
import { useAuth } from '@/hooks/useAuth';
import type { PhotoListItem, AlbumListItem, Friend } from '@/lib/api';
import type { Photo } from '@/types/photo';

interface AlbumDetailPageProps {
  albumId: string;
}

export function AlbumDetailPage({ albumId }: AlbumDetailPageProps) {
  const navigate = useNavigate();
  const { data: album, isLoading, error } = useAlbum(albumId);
  const {
    data: albumPhotosData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAlbumPhotos(albumId);
  const { data: collaborators } = useCollaborators(albumId);
  const { data: userPlan } = useUserPlan();
  const { user } = useAuth();

  const [selectedPhoto, setSelectedPhoto] = useState<PhotoListItem | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddPhotosDialog, setShowAddPhotosDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Check if this is a collaborative album and user permissions
  const isCollaborative = collaborators?.some((c) => c.role === 'contributor') ?? false;
  const userIsOwner = isAlbumOwner(collaborators, user?.id);
  const canShare = !isFreePlan(userPlan?.planType ?? 'free');

  // Flatten infinite query pages into a single photos array
  const photos = useMemo(() => {
    return albumPhotosData?.pages.flatMap((page) => page.photos ?? []) ?? album?.photos ?? [];
  }, [albumPhotosData, album?.photos]);

  // Get existing photo IDs for the add photos dialog
  const existingPhotoIds = useMemo(() => {
    return photos.map((p) => p.id);
  }, [photos]);

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  // Convert album to AlbumListItem for dialogs
  const albumListItem: AlbumListItem | null = album
    ? {
        id: album.id,
        userId: album.userId,
        name: album.name,
        description: album.description,
        coverPhotoId: album.coverPhotoId,
        photoCount: album.photoCount,
        isCollaborative: album.isCollaborative,
        createdAt: album.createdAt,
        updatedAt: album.updatedAt,
        coverPhotoUrl: album.coverPhotoUrl,
      }
    : null;

  // Convert PhotoListItem to Photo type for PhotoModal
  const selectedPhotoForModal: Photo | null = selectedPhoto
    ? {
        id: selectedPhoto.id,
        filename: selectedPhoto.originalFilename || 'Photo',
        thumbnailUrl: selectedPhoto.thumbnailUrl || '',
        originalUrl: selectedPhoto.imageUrl || '',
        createdAt: new Date(selectedPhoto.createdAt * 1000).toISOString(),
        takenAt: selectedPhoto.takenAt
          ? new Date(selectedPhoto.takenAt * 1000).toISOString()
          : undefined,
        width: selectedPhoto.width ?? undefined,
        height: selectedPhoto.height ?? undefined,
        fileSize: selectedPhoto.fileSize,
        worldId: selectedPhoto.worldId ?? undefined,
        worldName: selectedPhoto.worldName,
        instanceType: selectedPhoto.instanceType ?? undefined,
        region: selectedPhoto.region ?? undefined,
        authorName: selectedPhoto.authorName ?? undefined,
        friends: selectedPhoto.friends,
      }
    : null;

  const photosForModal: Photo[] = useMemo(
    () =>
      photos.map((item) => ({
        id: item.id,
        filename: item.originalFilename || 'Photo',
        originalFilename: item.originalFilename ?? undefined,
        thumbnailUrl: item.thumbnailUrl || '',
        originalUrl: item.imageUrl || '',
        createdAt: new Date(item.createdAt * 1000).toISOString(),
        takenAt: item.takenAt ? new Date(item.takenAt * 1000).toISOString() : undefined,
        width: item.width ?? undefined,
        height: item.height ?? undefined,
        fileSize: item.fileSize,
        worldId: item.worldId ?? undefined,
        worldName: item.worldName,
        instanceType: item.instanceType ?? undefined,
        region: item.region ?? undefined,
        authorName: item.authorName ?? undefined,
        friends: item.friends,
      })),
    [photos]
  );

  const handleNavigate = useCallback(
    (photo: Photo) => {
      const found = photos.find((p) => p.id === photo.id);
      if (found) setSelectedPhoto(found);
    },
    [photos]
  );

  const handlePlayerRemoved = useCallback((vrchatId: string) => {
    setSelectedPhoto((prev) => {
      if (!prev) return prev;
      return { ...prev, friends: prev.friends?.filter((f) => f.vrchatId !== vrchatId) || [] };
    });
  }, []);

  const handlePlayerAdded = useCallback((player: Friend) => {
    setSelectedPhoto((prev) => {
      if (!prev) return prev;
      return { ...prev, friends: [...(prev.friends || []), player] };
    });
  }, []);

  const handleDeleteSuccess = () => {
    navigate({ to: '/albums' });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="mb-8 flex items-center gap-4">
            <div className="h-10 w-10 rounded bg-muted" />
            <div className="h-8 w-48 rounded bg-muted" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !album) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <h2 className="text-xl font-semibold">アルバムが見つかりません</h2>
          <p className="mt-2 text-muted-foreground">
            お探しのアルバムは存在しないか、アクセス権限がありません。
          </p>
          <Button className="mt-4" onClick={() => navigate({ to: '/albums' })}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            アルバム一覧に戻る
          </Button>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(album.createdAt * 1000).toLocaleDateString();

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate({ to: '/albums' })}
          aria-label="アルバム一覧に戻る"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          アルバム一覧に戻る
        </Button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{album.name}</h1>
            {album.description && <p className="mt-2 text-muted-foreground">{album.description}</p>}
            <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <ImageIcon className="h-4 w-4" aria-hidden="true" />
                {album.photoCount}枚
              </span>
              <span>作成日 {formattedDate}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {canShare && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareDialog(true)}
                aria-label="アルバムを共有"
              >
                <Share2 className="mr-2 h-4 w-4" aria-hidden="true" />
                共有
              </Button>
            )}
            {isCollaborative && userIsOwner && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInviteDialog(true)}
                aria-label="コラボレーターを招待"
              >
                <Users className="mr-2 h-4 w-4" aria-hidden="true" />
                招待
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddPhotosDialog(true)}
              aria-label="写真を追加"
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              写真を追加
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditDialog(true)}
              aria-label="アルバムを編集"
            >
              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
              編集
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-destructive hover:text-destructive"
              aria-label="アルバムを削除"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              削除
            </Button>
          </div>
        </div>
      </div>

      {/* Collaborators Panel (for collaborative albums) */}
      {isCollaborative && (
        <div className="mb-8">
          <CollaboratorsPanel albumId={albumId} onInviteClick={() => setShowInviteDialog(true)} />
        </div>
      )}

      {/* Photos Grid with Drag & Drop */}
      <SortableAlbumPhotos albumId={albumId} photos={photos} onPhotoClick={setSelectedPhoto} />

      {/* Load More Button */}
      {hasNextPage && (
        <div className="mt-8 flex justify-center">
          <Button variant="outline" onClick={handleLoadMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? '読み込み中...' : 'もっと読み込む'}
          </Button>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhotoForModal && (
        <PhotoModal
          photo={selectedPhotoForModal}
          photos={photosForModal}
          isOpen={!!selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          onNavigate={handleNavigate}
          onPlayerRemoved={handlePlayerRemoved}
          onPlayerAdded={handlePlayerAdded}
          showMetadata
        />
      )}

      {/* Dialogs */}
      <EditAlbumDialog
        album={albumListItem}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      />

      <DeleteAlbumDialog
        album={albumListItem}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onSuccess={handleDeleteSuccess}
      />

      <AddPhotosToAlbumDialog
        albumId={albumId}
        albumName={album.name}
        existingPhotoIds={existingPhotoIds}
        open={showAddPhotosDialog}
        onOpenChange={setShowAddPhotosDialog}
      />

      {/* Album Share Dialog */}
      {canShare && (
        <AlbumShareDialog
          albumId={albumId}
          albumName={album.name}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
        />
      )}

      {/* Invitation Dialog */}
      {isCollaborative && userIsOwner && (
        <InvitationDialog
          albumId={albumId}
          albumName={album.name}
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
        />
      )}
    </div>
  );
}
